import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Shield, AlertTriangle, AlertOctagon, Activity, Database } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error("Введите адрес для поиска");
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/search/${encodeURIComponent(searchQuery.trim())}`);
      if (response.data.found) {
        navigate(`/address/${response.data.address.address_id}`);
      } else {
        toast.error("Адрес не найден в базе данных");
      }
    } catch (error) {
      toast.error("Ошибка поиска");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="header-blur border-b border-slate-200 sticky top-0 z-50" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">ChainScope</h1>
              <p className="text-[10px] sm:text-xs text-slate-500">Blockchain Intelligence</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600 mb-4 sm:mb-6">
            <Database className="w-3 h-3" />
            Мультисетевой анализ
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4 sm:mb-6">
            Анализ рисков<br />
            <span className="text-slate-500">блокчейн адресов</span>
          </h2>
          
          <p className="text-sm sm:text-base text-slate-600 mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
            Отслеживайте происхождение криптовалютных активов, анализируйте маршруты транзакций 
            и оценивайте уровень риска адресов для KYC/AML проверок.
          </p>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8 sm:mb-16 px-2">
            <div className="relative flex flex-col sm:flex-row gap-2 sm:gap-0">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Введите адрес кошелька..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 sm:pr-32 h-12 sm:h-14 text-sm sm:text-base font-mono border-slate-200 focus:border-slate-900 focus:ring-slate-900 w-full"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isSearching}
                className="sm:absolute sm:right-2 sm:top-1/2 sm:-translate-y-1/2 bg-slate-900 hover:bg-slate-800 h-12 sm:h-10 w-full sm:w-auto"
                data-testid="search-btn"
              >
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Поиск"
                )}
              </Button>
            </div>
          </form>

          {/* Risk Categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="risk-categories">
            {/* Low Risk */}
            <Card className="border-emerald-200 bg-emerald-50/50 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-emerald-700">Низкий риск</h3>
                    <p className="text-xs text-emerald-600">Low Risk</p>
                  </div>
                </div>
                <p className="text-sm text-emerald-700/80 text-left">
                  Биржи с KYC, майнинг, собственные средства. Чистые активы без ограничений.
                </p>
              </CardContent>
            </Card>

            {/* Medium Risk */}
            <Card className="border-amber-200 bg-amber-50/50 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-amber-700">Средний риск</h3>
                    <p className="text-xs text-amber-600">Medium Risk</p>
                  </div>
                </div>
                <p className="text-sm text-amber-700/80 text-left">
                  P2P-обменники, DEX, транзитные кошельки. Требует дополнительной проверки (EDD).
                </p>
              </CardContent>
            </Card>

            {/* High Risk */}
            <Card className="border-rose-200 bg-rose-50/50 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                    <AlertOctagon className="w-5 h-5 text-rose-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-rose-700">Высокий риск</h3>
                    <p className="text-xs text-rose-600">High Risk</p>
                  </div>
                </div>
                <p className="text-sm text-rose-700/80 text-left">
                  Миксеры, даркнет, санкционные списки (OFAC). Активы блокируются.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900 mb-1">ETH</div>
              <div className="text-sm text-slate-500">Ethereum</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900 mb-1">BTC</div>
              <div className="text-sm text-slate-500">Bitcoin</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900 mb-1">TRX</div>
              <div className="text-sm text-slate-500">Tron</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-900 mb-1">+10</div>
              <div className="text-sm text-slate-500">Сетей</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          © 2026 ChainScope. Blockchain Intelligence Platform.
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
